import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Grid, Card, CardContent, CardActions,
  CircularProgress, Alert, Chip, Divider, IconButton
} from '@mui/material';
import { Add as AddIcon, FolderOpen as FolderIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { projectService } from '../../services/projectService';

const ProjectList = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await projectService.getProjects();
      setProjects(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleCreateProject = () => {
    navigate('/project/new');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Projects
        </Typography>
        <Button
          variant="contained"
          color="success"
          startIcon={<AddIcon />}
          onClick={handleCreateProject}
        >
          Add Project
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      {projects.length === 0 && !error ? (
        <Alert severity="info" action={
          <Button color="inherit" size="small" onClick={handleCreateProject}>CREATE ONE</Button>
        }>
          No projects found. Add a project to get started.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {projects.map((project) => (
            <Grid item xs={12} sm={6} md={4} key={project._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                    cursor: 'pointer'
                  }
                }}
                onClick={() => {
                  const features = project.enabledFeatures || ['traffic'];
                  if (features.includes('crm')) navigate(`/project/${project._id}/contact`);
                  else if (features.includes('automations')) navigate(`/project/${project._id}/automation`);
                  else navigate(`/project/${project._id}/contactdashboard`);
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FolderIcon color="primary" />
                      <Typography variant="h6" component="h2" noWrap sx={{ maxWidth: '200px' }}>
                        {project.name}
                      </Typography>
                    </Box>
                    <Chip
                      label={project.status || 'unknown'}
                      color={project.status === 'active' ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={1} textAlign="center">
                    <Grid item xs={4}>
                      <Typography variant="h5" color="primary.main">{project.activitycount || 0}</Typography>
                      <Typography variant="caption" color="text.secondary">Activities</Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ borderLeft: '1px solid #eee', borderRight: '1px solid #eee' }}>
                      <Typography variant="h5" color="primary.main">{project.contactcount || 0}</Typography>
                      <Typography variant="caption" color="text.secondary">Contacts</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="h5" color="primary.main">{project.anonymouscount || 0}</Typography>
                      <Typography variant="caption" color="text.secondary">Visitors</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ProjectList;
